// Notificaciones push (Firebase Cloud Messaging) — lado cliente.
// El opt-in es explícito: el usuario activa push desde EditProfile o el PushPrompt.
// Mientras users.fcmTokens esté vacío, el backend no envía push (la presencia del token ES el opt-in).
import { getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging';
import { doc, setDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { snapshotMigrationConfig } from './migrationConfig';
import { registerDevice as apiRegisterDevice, revokeDevice as apiRevokeDevice, updateSelf as apiUpdateSelf } from '../api/users';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const LS_TOKEN_KEY = 'fcmToken';

// Config pública pasada al SW por query string (no son secretos).
const SW_PARAMS = new URLSearchParams({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
}).toString();

/** ¿El navegador soporta push y tenemos VAPID key configurada? */
export async function isPushSupported() {
  try {
    if (!VAPID_KEY) return false;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;
    return await isSupported();
  } catch {
    return false;
  }
}

/** Estado actual del permiso del navegador: 'default' | 'granted' | 'denied' | 'unsupported' */
export function pushPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

async function registerSW() {
  // Scope propio y aislado para no colisionar con el SW de workbox (/service-worker.js)
  // que controla offline/PWA en scope '/'. Dos SW en el mismo scope se reemplazan entre sí.
  const registration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${SW_PARAMS}`,
    { scope: '/firebase-cloud-messaging-push-scope' }
  );
  // register() resuelve ANTES de que el SW se active (installing → activated).
  // getToken() exige un SW activo o lanza "no active Service Worker"; esperamos.
  if (!registration.active) {
    await new Promise((resolve) => {
      const sw = registration.installing || registration.waiting;
      if (!sw) return resolve();
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated') resolve();
      });
    });
  }
  return registration;
}

/**
 * Pide permiso, obtiene el token FCM y lo guarda en users/{uid}.fcmTokens.
 * @returns {Promise<{ ok: boolean, reason?: 'denied'|'unsupported'|'error' }>}
 */
export async function enablePush(uid) {
  if (!uid) return { ok: false, reason: 'error' };
  if (!(await isPushSupported())) return { ok: false, reason: 'unsupported' };

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  try {
    const registration = await registerSW();
    const messaging = getMessaging(getApp());
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (!token) return { ok: false, reason: 'error' };

    // El token es el opt-in real: persistirlo de inmediato, antes de cualquier escritura
    // de prefs que pudiera fallar y dejar el dispositivo en estado "granted pero sin token".
    localStorage.setItem(LS_TOKEN_KEY, token);
    const snap = await snapshotMigrationConfig();
    if (snap.readsFrom('users') === 'postgres') {
      // En el API son DOS llamadas donde en Firestore había una sola escritura
      // atómica, y el ORDEN es el que ya pedía el comentario de arriba: primero
      // el dispositivo (que ES el opt-in real), después la preferencia. Si la
      // preferencia falla, el usuario queda registrado para recibir push y el
      // interruptor apagado — que es el fallo hacia el lado inocuo. Al revés
      // dejaría el interruptor encendido sin ningún teléfono al que mandar.
      await apiRegisterDevice(token);
      await apiUpdateSelf({ notifyByPush: true });
    } else {
      await setDoc(doc(db, 'users', uid), {
        fcmTokens: arrayUnion(token),
        notificationPrefs: { push: true },
      }, { merge: true });
    }
    return { ok: true };
  } catch (e) {
    console.error('enablePush error:', e);
    return { ok: false, reason: 'error' };
  }
}

/**
 * Desactiva push en este dispositivo: borra el token FCM y lo quita de fcmTokens.
 */
export async function disablePush(uid) {
  if (!uid) return;
  const stored = localStorage.getItem(LS_TOKEN_KEY);
  try {
    if (await isPushSupported()) {
      const messaging = getMessaging(getApp());
      await deleteToken(messaging).catch(() => {});
    }
  } catch { /* noop */ }

  try {
    if (stored) {
      const snap = await snapshotMigrationConfig();
      if (snap.readsFrom('users') === 'postgres') await apiRevokeDevice(stored);
      else await updateDoc(doc(db, 'users', uid), { fcmTokens: arrayRemove(stored) });
    }
  } catch (e) {
    console.error('disablePush error:', e);
  }
  localStorage.removeItem(LS_TOKEN_KEY);
}

/**
 * Navega dentro de la SPA cuando el usuario toca una notificación (el SW de FCM
 * no puede llamar navigate() sobre páginas controladas por el SW de workbox,
 * así que manda un postMessage y la app navega por dentro).
 * Devuelve la función para desuscribirse.
 */
export function listenPushClickNav(onNavigate) {
  if (!('serviceWorker' in navigator)) return () => {};
  const handler = (event) => {
    if (event.data?.type === 'PUSH_CLICK_NAV' && event.data.path) onNavigate(event.data.path);
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

/**
 * Muestra un toast cuando llega un push estando la app en primer plano
 * (en background lo maneja el SW). Devuelve la función para desuscribirse.
 */
export function listenForegroundPush(onMessageReceived) {
  let unsubscribe = null;
  let cancelled = false;
  isPushSupported().then((supported) => {
    if (!supported || cancelled) return;
    const messaging = getMessaging(getApp());
    unsubscribe = onMessage(messaging, (payload) => {
      onMessageReceived?.(payload);
    });
  });
  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
}
